import {
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryDecompositionResult,
  type CrossClientQueryEvaluation,
  type CrossClientQueryEvaluationType,
  type CrossClientQueryInitializationResult,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryResultSummary,
} from '../../models/CrossClient'
import { type BaseRequest } from '../models'
import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _TRANSCRIPTION_SERVICE_BASE_URL = import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!

interface CrossClientQueryDecomposeRequest extends BaseRequest {
  payload: {
    userQuestion: string
  }
}

interface CrossClientQueryInitializeUserQuestionPayload {
  userQuestion: string
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  sources?: CrossClientQueryDataSource[]
  clientQueryMode?: CrossClientQueryClientQueryMode
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  predefinedQueryId?: never
}

interface CrossClientQueryInitializePredefinedQueryPayload {
  predefinedQueryId: string
  sources?: CrossClientQueryDataSource[]
  clientQueryMode?: CrossClientQueryClientQueryMode
  userQuestion?: never
  decomposition?: never
  nnfDecomposition?: never
}

interface CrossClientQueryInitializeRequest extends BaseRequest {
  payload: CrossClientQueryInitializeUserQuestionPayload | CrossClientQueryInitializePredefinedQueryPayload
}

interface CrossClientQueryEvaluateResolversRequest extends BaseRequest {
  executionId: string
}

interface CreateCrossClientQueryEvaluationRequest extends BaseRequest {
  executionId: string
  type: CrossClientQueryEvaluationType
  resultIds: string[]
}

interface CancelCrossClientQueryEvaluationRequest extends BaseRequest {
  executionId: string
  evaluationId: string
}

export const crossClientExecutionApi = createApi({
  reducerPath: 'crossClientExecutionApi',
  baseQuery: baseQueryWithTokenRefresh(_TRANSCRIPTION_SERVICE_BASE_URL),
  endpoints: (builder) => ({
    decomposeCrossClientQuery: builder.mutation<CrossClientQueryDecompositionResult, CrossClientQueryDecomposeRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/cross-client-query/decompose`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: CrossClientQueryDecompositionResult }) => response.data,
    }),
    initializeCrossClientQuery: builder.mutation<CrossClientQueryInitializationResult, CrossClientQueryInitializeRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/cross-client-query/initialize`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: CrossClientQueryInitializationResult }) => response.data,
    }),
    evaluateClauseResolversCrossClientQuery: builder.mutation<CrossClientQueryResultSummary, CrossClientQueryEvaluateResolversRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluate-resolvers`,
        method: 'POST',
      }),
      transformResponse: (response: { data: CrossClientQueryResultSummary }) => response.data,
    }),
    createCrossClientQueryEvaluation: builder.mutation<CrossClientQueryEvaluation, CreateCrossClientQueryEvaluationRequest>({
      query: ({ accountId, executionId, type, resultIds }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluations`,
        method: 'POST',
        body: { type, resultIds },
      }),
      transformResponse: (response: { data: CrossClientQueryEvaluation }) => response.data,
    }),
    cancelCrossClientQueryEvaluation: builder.mutation<void, CancelCrossClientQueryEvaluationRequest>({
      query: ({ accountId, executionId, evaluationId }) => ({
        url: `/accounts/${accountId}/cross-client-query/${executionId}/evaluations/${evaluationId}/cancel`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useDecomposeCrossClientQueryMutation,
  useInitializeCrossClientQueryMutation,
  useEvaluateClauseResolversCrossClientQueryMutation,
  useCreateCrossClientQueryEvaluationMutation,
  useCancelCrossClientQueryEvaluationMutation,
} = crossClientExecutionApi
